import React, {forwardRef, useCallback, useEffect, useId, useImperativeHandle, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Check, ChevronsUpDown} from 'lucide-react';

// Drop-in replacement for a native <select>, styled like a macOS / iOS 26 pop-up
// menu: frosted-glass panel, rounded highlight, checkmark gutter. The menu is
// portalled to <body> so it is never clipped by a modal or a scroll container.
//
// - Options come from `options` ([{value, label, disabled, display}]) or from <option> /
//   <optgroup> children, exactly like a native select.
// - onChange receives a select-like event ({target: {name, value}}) so existing
//   `handleChange(e)` handlers keep working unchanged.
// - Works with react-hook-form's `{...register(name)}`: the forwarded ref exposes
//   a `value` RHF can read and write, and `focus()` for error focusing.
// - The component owns its look: `className` only carries layout (width, margins,
//   flex…); colours, borders and padding from an old select class are ignored.

const MENU_GAP = 6;
const MENU_MAX_HEIGHT = 288;

const LAYOUT_CLASS = /^!?(?:[a-z0-9]+:)*(?:-?m[trblxy]?-|w-|min-w-|max-w-|h-|flex-|grow|shrink|basis-|col-|row-|self-|order-|hidden$|block$|flex$|inline-flex$|inline-block$)/;
const layoutClasses = (className) => className.split(/\s+/).filter(c => LAYOUT_CLASS.test(c)).join(' ');

const textOf = (node) => {
    if (node === null || node === undefined || typeof node === 'boolean') return '';
    if (Array.isArray(node)) return node.map(textOf).join('');
    if (React.isValidElement(node)) return textOf(node.props.children);
    return String(node);
};

// Flattens <option>, <optgroup>, fragments and mapped arrays into a flat list
const optionsFromChildren = (children, group) => {
    const out = [];
    React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return;
        if (child.type === React.Fragment) {
            out.push(...optionsFromChildren(child.props.children, group));
        } else if (child.type === 'optgroup') {
            out.push({header: true, label: child.props.label});
            out.push(...optionsFromChildren(child.props.children, child.props.label));
        } else if (child.type === 'option') {
            const label = textOf(child.props.children);
            out.push({
                value: child.props.value !== undefined ? child.props.value : label,
                label,
                disabled: !!child.props.disabled,
                hidden: !!child.props.hidden,
            });
        }
    });
    return out;
};

const AppleSelect = forwardRef(({
                                    id,
                                    name,
                                    value: controlledValue,
                                    defaultValue,
                                    onChange,
                                    onBlur,
                                    options: optionsProp,
                                    children,
                                    placeholder,
                                    disabled = false,
                                    error = false,
                                    icon: Icon,
                                    size = 'md',
                                    variant = 'field',
                                    className = '',
                                    title,
                                    'aria-label': ariaLabel,
                                }, ref) => {
    const allOptions = optionsProp || optionsFromChildren(children);
    const items = allOptions.filter(o => !o.hidden);
    const selectable = items.filter(o => !o.header);

    const isControlled = controlledValue !== undefined;
    const [innerValue, setInnerValue] = useState(() =>
        defaultValue !== undefined ? defaultValue : (selectable[0]?.value ?? ''));
    const value = isControlled ? controlledValue : innerValue;
    const valueRef = useRef(value);
    valueRef.current = value;

    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const [position, setPosition] = useState(null);
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const typeahead = useRef({text: '', timer: null});
    const listId = useId();

    // Seen by react-hook-form as the field element
    useImperativeHandle(ref, () => ({
        name,
        get value() { return valueRef.current; },
        set value(v) { setInnerValue(v ?? ''); },
        focus: () => buttonRef.current?.focus(),
        blur: () => buttonRef.current?.blur(),
        scrollIntoView: (opts) => buttonRef.current?.scrollIntoView(opts),
    }), [name]);

    // Native selects compare as strings: "4" and 4 select the same option. Like a
    // native select, an unmatched value shows the first option (unless a placeholder is set).
    const selectedIndex = items.findIndex(o => !o.header && String(o.value) === String(value ?? ''));
    const selected = selectedIndex >= 0
        ? items[selectedIndex]
        : (placeholder ? null : selectable[0] || null);
    const isPlaceholder = !selected || (selected.value === '' && !!placeholder);

    const place = useCallback(() => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (!rect) return;
        const width = Math.max(rect.width, 180);
        const below = window.innerHeight - rect.bottom - MENU_GAP - 8;
        const above = rect.top - MENU_GAP - 8;
        const flip = below < Math.min(MENU_MAX_HEIGHT, 180) && above > below;
        setPosition({
            left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
            minWidth: width,
            maxHeight: Math.min(MENU_MAX_HEIGHT, flip ? above : below),
            ...(flip ? {bottom: window.innerHeight - rect.top + MENU_GAP} : {top: rect.bottom + MENU_GAP}),
            origin: flip ? 'bottom' : 'top',
        });
    }, []);

    const firstEnabled = (from, step) => {
        for (let i = 1; i <= items.length; i++) {
            const next = ((from + step * i) % items.length + items.length) % items.length;
            if (!items[next].header && !items[next].disabled) return next;
        }
        return from;
    };

    const openMenu = () => {
        if (disabled) return;
        place();
        setActive(selectedIndex >= 0 ? selectedIndex : firstEnabled(-1, 1));
        setOpen(true);
    };

    const close = (refocus = true) => {
        setOpen(false);
        onBlur?.({target: {name, value: valueRef.current}, type: 'blur'});
        if (refocus) buttonRef.current?.focus();
    };

    const choose = (option) => {
        if (option.disabled || option.header) return;
        if (String(option.value) !== String(value ?? '')) {
            if (!isControlled) setInnerValue(option.value);
            valueRef.current = option.value;
            const target = {name, id, value: String(option.value), type: 'select-one'};
            onChange?.({target, currentTarget: target, type: 'change', preventDefault() {}, stopPropagation() {}});
        }
        close();
    };

    useLayoutEffect(() => {
        if (!open) return;
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [open, place]);

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (!menuRef.current?.contains(e.target) && !buttonRef.current?.contains(e.target)) close(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    });

    // Keep the highlighted row visible while navigating with the keyboard
    useEffect(() => {
        if (open && active >= 0) {
            menuRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView?.({block: 'nearest'});
        }
    }, [open, active]);

    const onKeyDown = (e) => {
        if (disabled) return;
        if (!open) {
            if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
                e.preventDefault();
                openMenu();
            }
            return;
        }
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActive(a => firstEnabled(a, 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActive(a => firstEnabled(a, -1));
                break;
            case 'Home':
                e.preventDefault();
                setActive(firstEnabled(-1, 1));
                break;
            case 'End':
                e.preventDefault();
                setActive(firstEnabled(items.length, -1));
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (items[active]) choose(items[active]);
                break;
            case 'Escape':
                e.preventDefault();
                e.stopPropagation(); // don't close the surrounding modal
                close();
                break;
            case 'Tab':
                close(false);
                break;
            default:
                if (e.key.length === 1) {
                    const t = typeahead.current;
                    clearTimeout(t.timer);
                    t.text += e.key.toLowerCase();
                    t.timer = setTimeout(() => { t.text = ''; }, 600);
                    const hit = items.findIndex(o => !o.header && !o.disabled && String(o.label).toLowerCase().startsWith(t.text));
                    if (hit >= 0) setActive(hit);
                }
        }
    };

    const compact = size === 'sm';
    const toolbar = variant === 'toolbar';
    const layout = layoutClasses(className);
    const hasWidth = /(^|\s|:)!?w-/.test(layout);
    const width = hasWidth ? '' : (!className && !compact) || toolbar ? 'w-full' : '';

    // Toolbar: a square cell of the edge-to-edge search bars (same height, border
    // and inset focus ring as SearchInput), stretched to the row height.
    const look = toolbar
        ? `self-stretch h-full min-h-[3.125rem] rounded-none py-3 pl-3 sm:pl-4 pr-3 text-sm font-medium bg-white dark:bg-gray-800
           border-gray-200 dark:border-gray-700 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700
           focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary
           ${open ? 'ring-2 ring-inset ring-primary' : ''}`
        : `rounded-xl border bg-white/80 dark:bg-gray-800/80 backdrop-blur hover:bg-white dark:hover:bg-gray-800
           shadow-[0_1px_2px_rgba(0,0,0,0.06)] active:scale-[0.99] disabled:active:scale-100
           focus:outline-none focus-visible:ring-4
           ${compact ? 'py-1.5 pl-3 pr-2 text-sm' : 'py-2 sm:py-2.5 pl-3 sm:pl-4 pr-2.5 text-sm sm:text-base'}
           ${error
               ? 'border-red-500 dark:border-red-700 focus-visible:ring-red-500/25'
               : `border-gray-300 dark:border-gray-600 focus-visible:ring-primary/25 focus-visible:border-primary ${open ? '!border-primary ring-4 ring-primary/20' : ''}`}`;

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                id={id}
                name={name}
                title={title}
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={open ? listId : undefined}
                aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
                aria-label={ariaLabel}
                aria-invalid={error ? true : undefined}
                disabled={disabled}
                onClick={() => (open ? close() : openMenu())}
                onKeyDown={onKeyDown}
                className={`group relative inline-flex items-center gap-2 min-w-0 text-left border transition-all duration-150
                    disabled:opacity-60 disabled:cursor-not-allowed ${look} ${width} ${layout}`}
            >
                {Icon && <Icon className={`${toolbar ? 'w-5 h-5 text-gray-600 dark:text-gray-300' : 'w-4 h-4 text-gray-400'} shrink-0`} aria-hidden="true"/>}
                <span className={`flex-1 truncate ${isPlaceholder ? 'text-gray-400 dark:text-gray-500' : toolbar ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-white'}`}>
                    {selected ? (selected.display ?? selected.label) : placeholder || ' '}
                </span>
                <span className={`flex items-center justify-center shrink-0 rounded-md transition-colors
                    ${compact || toolbar ? 'w-5 h-5' : 'w-5 h-5 sm:w-6 sm:h-6'}
                    ${open ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 group-hover:bg-primary/15 group-hover:text-primary'}`}>
                    <ChevronsUpDown className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} strokeWidth={2.5} aria-hidden="true"/>
                </span>
            </button>

            {open && position && createPortal(
                <ul
                    ref={menuRef}
                    id={listId}
                    role="listbox"
                    aria-label={ariaLabel}
                    tabIndex={-1}
                    style={{
                        position: 'fixed',
                        left: position.left,
                        top: position.top,
                        bottom: position.bottom,
                        minWidth: position.minWidth,
                        maxWidth: 'calc(100vw - 16px)',
                        maxHeight: position.maxHeight,
                        transformOrigin: position.origin,
                    }}
                    className="apple-select-menu z-[10000] overflow-auto overscroll-contain p-1.5 rounded-2xl
                        bg-white/75 dark:bg-gray-800/75 backdrop-blur-2xl backdrop-saturate-150
                        border border-white/60 dark:border-white/10
                        shadow-[0_12px_40px_-8px_rgba(0,0,0,0.25),0_0_0_0.5px_rgba(0,0,0,0.08)]
                        dark:shadow-[0_12px_40px_-8px_rgba(0,0,0,0.6),0_0_0_0.5px_rgba(255,255,255,0.06)]"
                >
                    {items.map((option, i) => {
                        if (option.header) {
                            return (
                                <li key={`header-${i}`} role="presentation"
                                    className="px-2.5 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 select-none">
                                    {option.label}
                                </li>
                            );
                        }
                        const isSelected = i === selectedIndex;
                        const isActive = i === active;
                        return (
                            <li
                                key={`${option.value}-${i}`}
                                id={`${listId}-${i}`}
                                data-index={i}
                                role="option"
                                aria-selected={isSelected}
                                aria-disabled={option.disabled || undefined}
                                onMouseEnter={() => !option.disabled && setActive(i)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => choose(option)}
                                className={`flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-lg text-sm select-none whitespace-nowrap transition-colors duration-75
                                    ${option.disabled
                                        ? 'text-gray-300 dark:text-gray-600 cursor-default'
                                        : isActive
                                            ? 'bg-primary text-white cursor-pointer'
                                            : 'text-gray-800 dark:text-gray-100 cursor-pointer'}`}
                            >
                                <span className="w-4 flex justify-center shrink-0">
                                    {isSelected && <Check className="w-3.5 h-3.5" strokeWidth={3} aria-hidden="true"/>}
                                </span>
                                <span className={`truncate ${option.value === '' && placeholder && !isActive ? 'text-gray-400' : ''}`}>
                                    {option.label}
                                </span>
                            </li>
                        );
                    })}
                    {selectable.length === 0 && (
                        <li className="px-3 py-2 text-sm text-gray-400" role="presentation">Aucune option</li>
                    )}
                </ul>,
                document.body
            )}
        </>
    );
});

AppleSelect.displayName = 'AppleSelect';

export default AppleSelect;
